// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// tutorial.ts — eight steps on the real board, as DATA.
//
// IT DRIVES THE SHIPPED STATE MACHINE. There is no mock floor, no parallel rule set and no scripted
// animation: `setup()` builds a genuine Building and a genuine State through the real `createState`,
// and every step is satisfied by the player issuing a real action down the same path a live run uses.
// A tutorial with a private implementation is a lie the moment anybody touches the rules — and worse,
// it is not even a DETECTABLE lie, because it goes on confidently teaching the old game for ever.
// Because the steps are data, tests/tutorial.test.ts replays every one of them through the real
// simulation and asserts each is legal and reachable from the state the previous one produced.
//
// NO STEP INVENTS A RULE. The design's version refused a standing step into the doorway as "illegal",
// which is not a thing rules.ts has ever said — so that step was not driving the state machine at all,
// and its refusal could not be tested against the real rules. Step 4 keeps the standing step perfectly
// legal and teaches through the PREVIEW instead: the test asserts, through the real perception
// function, that standing in that doorway reads SEEN and creeping into it reads CLEAR. Step 5's
// refusal is real because MIN_THROW is a real entry in the real action table, shared by every mode.
//
// THE FLOOR IS HAND-AUTHORED, not generated, so no step can become unpassable on a reroll.
//
// THE LAST STEP IS THE POINT. Everything before it exists to reach the moment where you have made your
// own window — thrown a clatter behind the man watching the gap, walked through it while he goes to
// look, and now have to bank two cases before somebody walks over the empty one you left behind. That
// trade is the whole game and it is not something anybody learns from a rules card.

import { SLOTS, SLUGS_CARRIED, Verb } from './tuning';
import { idx, makeGrid, xOf, yOf } from './map';
import { Floor } from './tuning';
import { MODES } from './modes';
import {
  canDo,
  createState,
  isBright,
  seesIntruder,
  type Action,
  type Building,
  type State,
} from './rules';

// The floor, as a picture. '#' wall, '.' floor, and the two gaps in the internal walls are the only
// ways between the three rooms — which is what makes one body standing in a gap a real problem.
const PLAN = [
  '########',
  '#......#',
  '#......#',
  '###.#.##',
  '#......#',
  '#......#',
  '###.#.##',
  '#......#',
  '#......#',
  '########',
];

export const START = idx(1, 8);
export const CASE_NEAR = idx(5, 8);
/** The gap he is watching, and the one that makes creeping worth two slots a cell. */
export const GAP_LOW = idx(3, 6);
export const GAP_HIGH = idx(3, 3);
export const MINDER_AT = GAP_HIGH;
export const SLUG_AT = idx(3, 2);
/**
 * The breaker is in the room you START in, and that placement is load-bearing rather than incidental.
 * Put it in the room the minder is standing next to and he reads the GLOOM ROOM mark you just made,
 * walks over and puts the lights straight back on — which is the evidence loop working exactly as
 * designed, and which made the step impossible to complete. The lesson still lands here, because
 * gloom in this room is what makes the approach to the gap survivable.
 */
export const BREAKER_AT = idx(5, 7);
export const CASE_FAR = idx(1, 4);
/** Past the gap he is watching. Reaching it is the whole point of the window you make with the slug. */
export const CHUTE_AT = idx(5, 4);

/** Room ids: 0 is the top band, 1 the middle, 2 the bottom. The two gaps belong to no room. */
function roomOf(y: number): number {
  if (y >= 1 && y <= 2) return 0;
  if (y >= 4 && y <= 5) return 1;
  if (y >= 7 && y <= 8) return 2;
  return -1;
}

export function building(): Building {
  const g = makeGrid();
  g.wall.fill(1);
  for (let y = 0; y < PLAN.length; y++) {
    for (let x = 0; x < 8; x++) {
      if (PLAN[y][x] !== '.') continue;
      const c = idx(x, y);
      g.wall[c] = 0;
      g.room[c] = roomOf(y);
      // Row 5 is grating, so the slug lesson lands on a floor that is audibly different — the map you
      // read for space is the map you read for volume.
      g.floorT[c] = y === 5 ? Floor.Grating : y === 8 ? Floor.Tile : Floor.Carpet;
    }
  }
  g.roomCount = 3;
  const ducts: [number, number] = [idx(6, 8), idx(6, 1)];
  g.ductAt[ducts[0]] = 0;
  g.ductAt[ducts[1]] = 1;

  return {
    grid: g,
    doors: [],
    entry: START,
    exits: [START, idx(6, 7), ducts[1]],
    chutes: [CHUTE_AT, idx(1, 7)],
    ducts,
    breakers: [-1, -1, BREAKER_AT],
    items: [
      { id: 0, cell: CASE_NEAR, value: 10, taken: false, banked: false, confiscated: false },
      { id: 1, cell: CASE_FAR, value: 20, taken: false, banked: false, confiscated: false },
    ],
    // ONE body, standing in the upper gap and looking down the column at you. It holds its ground —
    // a one-cell loop makes patrolStep a no-op — because a scripted opponent that wanders off is a
    // tutorial whose steps stop being reachable.
    minderStarts: [{ cell: MINDER_AT, loop: [MINDER_AT], facing: 6 }],
    lensStarts: [],
    strongroom: idx(6, 1),
    anchors: [idx(1, 1), idx(1, 4), idx(1, 7)],
  };
}

export function setup(): State {
  return createState(building(), { ...MODES.nightshift, minders: 1, lenses: 0, loot: 2 }, 20260822);
}

export interface Step {
  id: string;
  /** One sentence, imperative, naming what to touch. */
  text: string;
  /** Said once it is done — the WHY, after the player has seen the WHAT. */
  after: string;
  /** A cell to ring on the board, or -1. */
  hint(s: State): number;
  /** Is this action allowed right now? Everything else is refused without breaking the sequence. */
  allows(s: State, a: Action): boolean;
  /** Shown when something else is attempted. */
  refusal: string;
  /** Has the player done the thing? */
  done(s: State, staged: readonly Action[]): boolean;
}

const isMove = (a: Action): boolean => a.verb === Verb.Step || a.verb === Verb.Creep || a.verb === Verb.Bolt;
const holds = (s: State, id: number): boolean => s.carrying.includes(id) || s.banked.includes(id);

export const STEPS: readonly Step[] = [
  {
    id: 'preview',
    text: 'Tap the green case along the bottom of the room.',
    after: 'That is the whole control scheme: tap where you want to be, and the route tells you what it costs before you commit to it.',
    hint: () => CASE_NEAR,
    allows: () => true,
    done: (_s, staged) => staged.some((a) => isMove(a) && a.arg === CASE_NEAR),
    refusal: 'Tap the green case.',
  },
  {
    id: 'commit',
    text: 'Tap GO to walk it.',
    after: 'Five slots a turn. Every one you spend is a slot the building gets to use as well.',
    hint: () => CASE_NEAR,
    allows: (_s, a) => isMove(a),
    done: (s) => s.pos === CASE_NEAR,
    refusal: 'Tap GO.',
  },
  {
    id: 'grab',
    text: 'Tap the case to take it.',
    after: 'And you have left a gap where it was. That gap is evidence, and evidence is what this building actually hunts.',
    hint: () => CASE_NEAR,
    allows: (_s, a) => a.verb === Verb.Grab || isMove(a),
    done: (s) => holds(s, 0),
    refusal: 'Tap the case you are standing on.',
  },
  {
    id: 'breaker',
    text: 'Tap the breaker to kill this room’s lights.',
    after: 'Watch the blue arc shrink. Gloom takes two cells off how far anybody can see into here — light is half of that number.',
    hint: () => BREAKER_AT,
    allows: (_s, a) => isMove(a) || a.verb === Verb.Breaker,
    done: (s) => s.roomGloom[2] === 1,
    refusal: 'Get next to the breaker, then tap it.',
  },
  {
    id: 'creep',
    text: 'Tap CREEP, then move into the gap in the wall.',
    after: 'Crouching takes two more cells off it. He is still looking straight down at that gap — he just cannot make you out in it any more.',
    hint: () => GAP_LOW,
    // The standing step is perfectly LEGAL. The lesson is the preview going red, not a rule that does
    // not exist — see the header.
    allows: (_s, a) => isMove(a),
    done: (s) => s.pos === GAP_LOW && s.posture === Verb.Creep,
    refusal: 'Tap CREEP first, then the gap.',
  },
  {
    id: 'slug',
    text: 'Tap SLUG, then the room BEHIND him.',
    after: 'Everyone who can hear it goes to look. Noise costs you no heat at all — it is the only free thing you have.',
    hint: () => SLUG_AT,
    allows: (_s, a) => a.verb === Verb.Slug,
    done: (s) => s.slugs < SLUGS_CARRIED,
    refusal: 'Throw it away from you, not at your feet.',
  },
  {
    id: 'bank',
    text: 'Now go through, and bank it at the ring.',
    after: 'That window was not luck. You made it — and the empty case you left downstairs is already walking towards somebody.',
    hint: () => CHUTE_AT,
    allows: (_s, a) => isMove(a) || a.verb === Verb.Bank,
    done: (s) => s.banked.length >= 1,
    refusal: 'Get to the ring past the gap, then tap BANK.',
  },
];

export const STEP_COUNT = STEPS.length;

/** Is this action permitted at step `i`? Out-of-range indices allow everything, so the end is playable. */
export function allowed(i: number, s: State, a: Action): boolean {
  const step = STEPS[i];
  if (!step) return true;
  if (!canDo(s, a, 0)) return false;
  return step.allows(s, a);
}

/**
 * The two facts step 4 rests on, computed through the REAL perception function so the lesson can never
 * drift from the rules. tests/tutorial.test.ts asserts both, and a mutation to the creep penalty turns
 * the tutorial red instead of leaving it teaching a game that no longer exists.
 */
export function creepLesson(): { stoodIsSeen: boolean; creptIsClear: boolean; bright: boolean } {
  const stood = setup();
  stood.pos = GAP_LOW;
  stood.posture = Verb.Step;
  const crept = setup();
  crept.pos = GAP_LOW;
  crept.posture = Verb.Creep;
  return {
    stoodIsSeen: stood.minders.some((m) => seesIntruder(stood, m)),
    creptIsClear: !crept.minders.some((m) => seesIntruder(crept, m)),
    bright: isBright(stood, GAP_LOW),
  };
}

/** Slots a step's action costs, so the UI can say whether it fits in what is left of the turn. */
export const fitsTurn = (staged: readonly Action[]): boolean =>
  staged.reduce((n, a) => n + (a.verb === Verb.Creep ? 2 : 1), 0) <= SLOTS;

export const cellName = (c: number): string => `${xOf(c)},${yOf(c)}`;
